import { useRef, useEffect } from 'react';
import { Bold, Italic } from 'lucide-react';
import { handleSmartPaste } from './sanitizePaste';

/**
 * RichInput — contentEditable with a small bold/italic toolbar.
 *
 * Supports:
 *   - Ctrl+B / Cmd+B  → bold (native contentEditable)
 *   - Ctrl+G / Cmd+G  → bold (FR keyboard shortcut)
 *   - Ctrl+I / Cmd+I  → italic (native)
 *   - Smart paste (sanitizePaste.js) : ne conserve QUE <strong> et <em>.
 *
 * Value is raw HTML (contentEditable.innerHTML). Parent is responsible for
 * converting to/from block structure.
 */
export default function RichInput({ value, onChange, placeholder, disabled, rows = 2, className = '' }) {
    const ref = useRef(null);

    // Keep the DOM in sync when the parent provides a new value (e.g. reset).
    // We guard against overwriting while the user is actively typing by comparing innerHTML.
    useEffect(() => {
        if (!ref.current) return;
        if (ref.current.innerHTML !== (value || '')) {
            ref.current.innerHTML = value || '';
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const execCmd = (cmd) => {
        // eslint-disable-next-line no-restricted-syntax
        document.execCommand(cmd, false, null);
        if (ref.current) onChange(ref.current.innerHTML);
    };

    const handleInput = () => {
        if (ref.current) onChange(ref.current.innerHTML);
    };

    const handlePaste = (e) => {
        handleSmartPaste(e);
        if (ref.current) onChange(ref.current.innerHTML);
    };

    // Ctrl+G (FR) maps to bold — Ctrl+B / Ctrl+I are handled natively by contentEditable.
    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G')) {
            e.preventDefault();
            execCmd('bold');
        }
    };

    return (
        <div className={`rich-input-wrap ${className}`}>
            <div className="rich-input-toolbar">
                <button type="button" className="rich-input-btn" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('bold')}
                    title="Gras (Ctrl+B / Ctrl+G)" disabled={disabled}>
                    <Bold size={12} />
                </button>
                <button type="button" className="rich-input-btn" onMouseDown={e => e.preventDefault()} onClick={() => execCmd('italic')}
                    title="Italique (Ctrl+I)" disabled={disabled}>
                    <Italic size={12} />
                </button>
            </div>
            <div
                ref={ref}
                className="rich-input"
                contentEditable={!disabled}
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                suppressContentEditableWarning
                style={{ minHeight: rows * 24 }}
                data-placeholder={placeholder}
            />
        </div>
    );
}
